import { useMutation, useQueryClient } from "@tanstack/vue-query";
import Unipile from "@/services/unipile/unipile";
import type { SendMessageToChatRequest } from "@/dtos/Unipile.dto";

export interface SendLinkedInMessageParams {
  conversationId: string;
  text?: string;
  quote_id?: string;
  attachments?: File[];
  voice_message?: File;
  video_message?: File;
  typing_duration?: number;
}

export function useSendLinkedInMessage() {
  const queryClient = useQueryClient();

  const sendMessageMutation = useMutation({
    mutationFn: async (params: SendLinkedInMessageParams) => {
      const requestPayload: SendMessageToChatRequest = {};

      if (params.text) {
        requestPayload.text = params.text;
      }

      if (params.quote_id) {
        requestPayload.quote_id = params.quote_id;
      }

      if (params.attachments && params.attachments.length > 0) {
        requestPayload.attachments = params.attachments;
      }

      if (params.voice_message) {
        requestPayload.voice_message = params.voice_message;
      }

      if (params.video_message) {
        requestPayload.video_message = params.video_message;
      }

      if (params.typing_duration) {
        requestPayload.typing_duration = String(params.typing_duration);
      }

      const response = await Unipile.sendMessage(params.conversationId, requestPayload);

      if (!response.data?.status) {
        const errorData = response.data as any;
        throw new Error(errorData?.message || errorData?.msg || "Failed to send message");
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["linkedin-messages", variables.conversationId],
      });

      queryClient.invalidateQueries({
        queryKey: ["linkedin-chat-by-contact"],
      });
    },
  });

  return {
    sendMessage: sendMessageMutation.mutateAsync,
    isSending: sendMessageMutation.isPending,
    sendError: sendMessageMutation.error,
  };
}
